/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { format, isBefore, startOfDay } from 'date-fns';
import get from 'lodash/get';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Button from 'carbon-react/lib/components/button';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import DateInput from 'carbon-react/lib/components/date';
import Form from 'carbon-react/lib/components/form';
import Icon from 'carbon-react/lib/components/icon';
import { MultiSelect, Option, Select } from 'carbon-react/lib/components/select';
import Textarea from 'carbon-react/lib/components/textarea';
import Textbox from 'carbon-react/lib/components/textbox';
import Tooltip from 'carbon-react/lib/components/tooltip';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { getOrdinalSuffix } from '@futrli/localisation/core/utils';
import {
  invalidateAwaitingReviewReports,
  invalidateLiveReports,
  invalidateReportSchedules
} from '@futrli/views/mutations/invalidateListingQueries';
import { Logger } from '@futrli/shared-services';
import { toastError, toastSuccess, UICarbonNumberField, UILoader } from '@futrli/ui-components';

import { AuthStoreState } from '../../../core/types';
import { createScheduleReport, updateScheduleReport } from '../../../core/api/reporting/report-scheduling';
import { EmailPreview } from '../email-preview/email-preview';
import { forceLocalDate } from '../../../utils/dates';
import { NormalizedReportScheduleItem } from '../document-library/scheduled-reports/scheduled-reports';
import { ReportingSidebar } from '../reporting-sidebar/reporting-sidebar';
import { trackEvent } from '../../../core/tracker';
import { TRACKING_EVENTS } from '../../../core/constants';
import { useMembersContext } from '../../members-context';
import { useOrganistionName } from '../../../hooks/useOrganisationName';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';

import * as styles from './schedule-report-dialog.scss';

interface Props {
  isOpen: boolean;
  handleClose: () => void;
  reportName?: string;
  reportId: string;
  initialData?: NormalizedReportScheduleItem;
  revalidateData?: () => Promise<void>;
  isLoading?: boolean;
}

enum Recurrence {
  DOES_NOT_REPEAT = 'do-not-repeat',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  ANNUALLY = 'annually'
}

enum RecurrenceEndType {
  ON_THIS_DAY = 'end-date',
  AFTER_N_OCCURANCES = 'end-after-occurrences',
  NO_END_DATE = 'does-not-end'
}

enum RecurrenceWeekDay {
  MONDAY = 'monday',
  TUESDAY = 'tuesday',
  WEDNESDAY = 'wednesday',
  THURSDAY = 'thursday',
  FRIDAY = 'friday',
  SATURDAY = 'saturday',
  SUNDAY = 'sunday'
}

const THE_LAST = 'the_last';

const MAX_NUMBER_OF_USERS = 30;

const REPEATING_FREQUENCY_QUARTERLY = 3;
const REPEATING_FREQUENCY_ANNUALLY = 12;

export const ScheduleReportDialog = ({
  isOpen,
  handleClose,
  reportName: initialReportName,
  reportId,
  initialData,
  revalidateData,
  isLoading: isInitialLoading
}: Props) => {
  const intl = useIntl();
  const [reportName, setReportName] = useState(initialReportName || '');
  const [recurrence, setRecurrence] = useState(Recurrence.DOES_NOT_REPEAT);
  const [selectedUsers, setSelectedUsers] = useState<Record<string, unknown>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [recurrenceStartDate, setRecurrenceStartDate] = useState(new Date());
  const [recurrenceStartDateError, setRecurrenceStartDateError] = useState(false);
  const [recurrenceRepeatValue, setRecurrenceRepeatValue] = useState('1');
  const [recurrenceEndType, setRecurrenceEndType] = useState(RecurrenceEndType.NO_END_DATE);
  const [occurancesValue, setOccurancesValue] = useState('10');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(new Date());
  const [recurrenceWeekDay, setRecurrenceWeekDay] = useState<RecurrenceWeekDay>(RecurrenceWeekDay.MONDAY);
  const [recurrenceMonthDay, setRecurrenceMonthDay] = useState(THE_LAST);
  const [reviewStep, setReviewStep] = useState(true);

  const [isOpenPreviewEmail, setIsOpenPreviewEmail] = useState(false);

  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const { isConsolidation } = usePredictionsInfoContext();

  const { isLoading: isMembersLoading, members } = useMembersContext();

  const organisationName = useOrganistionName();

  const fullName = useSelector((store: AuthStoreState) => {
    const user = store.authReducers.authenticatedUser;
    const givenName = get(user, 'attributes.given_name', '');
    const familyName = get(user, 'attributes.family_name', '');
    return !familyName ? givenName : `${givenName} ${familyName}`;
  });

  useEffect(() => {
    setEmailBody(
      intl.formatMessage(
        {
          id: 'reporting.email_body_v3',
          defaultMessage:
            'Hello,\n\nI’ve prepared the report {initialReportName} for you. Please find it attached as a PDF.\n\nAll the best,\n\n{fullName}'
        },
        { initialReportName, fullName }
      )
    );
  }, [fullName, initialReportName, intl]);

  useEffect(() => {
    setEmailSubject(
      intl.formatMessage(
        {
          id: 'predict.default_report_email_subject',
          defaultMessage: 'You have a new report for {organisationName}'
        },
        { organisationName }
      )
    );
  }, [organisationName, intl]);

  const memberOptions = useMemo(
    () =>
      (members || []).map(m => ({
        ...m,
        text: `${m.name.first_name} ${m.name.last_name} <${m.email_address}>`
      })),
    [members]
  );

  useEffect(() => {
    if (initialData && memberOptions) {
      setReportName(initialData.name);
      if (
        initialData.period === Recurrence.MONTHLY &&
        initialData.repeating_frequency === REPEATING_FREQUENCY_QUARTERLY
      ) {
        setRecurrence(Recurrence.QUARTERLY);
      } else if (
        initialData.period === Recurrence.MONTHLY &&
        initialData.repeating_frequency === REPEATING_FREQUENCY_ANNUALLY
      ) {
        setRecurrence(Recurrence.ANNUALLY);
      } else {
        setRecurrence(initialData.period as Recurrence);
      }
      const users = [];
      for (const email of initialData.users.split(', ')) {
        const user = memberOptions.find(u => u.email_address === email);
        if (user) {
          users.push(user);
        }
      }
      setSelectedUsers([...users]);
      setEmailSubject(initialData.email_subject);
      setEmailBody(initialData.email_body);
      setRecurrenceStartDate(forceLocalDate(initialData.start_date));
      setRecurrenceRepeatValue(String(initialData.repeating_frequency));
      setRecurrenceEndType(initialData.end_type as RecurrenceEndType);
      setOccurancesValue(
        typeof initialData.end_after_occurrences === 'number' ? String(initialData.end_after_occurrences) : '10'
      );
      setRecurrenceEndDate(forceLocalDate(initialData.end_date || ''));
      setRecurrenceWeekDay(initialData.weekday ? (initialData.weekday as RecurrenceWeekDay) : RecurrenceWeekDay.MONDAY);
      setRecurrenceMonthDay(
        initialData.day_of_month
          ? initialData.day_of_month === 31
            ? THE_LAST
            : String(initialData.day_of_month)
          : THE_LAST
      );
      setReviewStep(initialData.approval_email_user || false);

      const isStartRecurrenceInPast = isBefore(
        startOfDay(forceLocalDate(initialData.start_date)),
        startOfDay(new Date())
      );
      setRecurrenceStartDateError(isStartRecurrenceInPast);
    }
  }, [initialData, memberOptions]);

  const handleSchedulePdfReport = async () => {
    if (emailSubject && emailBody && selectedUsers.length) {
      const sharedPayloadData = {
        name: reportName,
        report: reportId,
        period:
          recurrence === Recurrence.QUARTERLY || recurrence === Recurrence.ANNUALLY ? Recurrence.MONTHLY : recurrence,
        start_date:
          recurrenceRepeatValue === Recurrence.DOES_NOT_REPEAT
            ? format(new Date(), 'yyyy-MM-dd')
            : format(recurrenceStartDate, 'yyyy-MM-dd'),
        email_subject: emailSubject,
        email_body: emailBody,
        users: selectedUsers.map(s => ({ id: s.id, email_address: s.email_address })),
        approval_email_user: recurrence === Recurrence.DOES_NOT_REPEAT ? false : reviewStep
      };

      let payload = {};
      if (recurrence === Recurrence.DAILY) {
        payload = {
          ...sharedPayloadData,
          repeating_frequency: Number(recurrenceRepeatValue),
          end_type: recurrenceEndType,
          end_after_occurrences:
            recurrenceEndType === RecurrenceEndType.AFTER_N_OCCURANCES ? Number(occurancesValue) : null,
          end_date:
            recurrenceEndType === RecurrenceEndType.ON_THIS_DAY ? format(recurrenceEndDate, 'yyyy-MM-dd') : null,
          day_of_month: null,
          weekday: null
        };
      } else if (
        recurrence === Recurrence.MONTHLY ||
        recurrence === Recurrence.QUARTERLY ||
        recurrence === Recurrence.ANNUALLY
      ) {
        payload = {
          ...sharedPayloadData,
          repeating_frequency: Number(recurrenceRepeatValue),
          day_of_month: recurrenceMonthDay === THE_LAST ? 31 : Number(recurrenceMonthDay),
          end_type: recurrenceEndType,
          end_after_occurrences:
            recurrenceEndType === RecurrenceEndType.AFTER_N_OCCURANCES ? Number(occurancesValue) : null,
          end_date:
            recurrenceEndType === RecurrenceEndType.ON_THIS_DAY ? format(recurrenceEndDate, 'yyyy-MM-dd') : null,
          weekday: null
        };
      } else if (recurrence === Recurrence.WEEKLY) {
        payload = {
          ...sharedPayloadData,
          repeating_frequency: Number(recurrenceRepeatValue),
          weekday: recurrenceWeekDay,
          end_type: recurrenceEndType,
          end_after_occurrences:
            recurrenceEndType === RecurrenceEndType.AFTER_N_OCCURANCES ? Number(occurancesValue) : null,
          end_date:
            recurrenceEndType === RecurrenceEndType.ON_THIS_DAY ? format(recurrenceEndDate, 'yyyy-MM-dd') : null,
          day_of_month: null
        };
      } else {
        payload = {
          ...sharedPayloadData,
          end_type: RecurrenceEndType.NO_END_DATE
        };
      }

      try {
        setIsLoading(true);
        if (initialData) {
          await updateScheduleReport(workspaceId, organisationId, initialData.id, payload, isConsolidation);
          invalidateReportSchedules(organisationId, workspaceId, isConsolidation);
        } else {
          await createScheduleReport(workspaceId, organisationId, payload, isConsolidation);
          invalidateReportSchedules(organisationId, workspaceId, isConsolidation);
          invalidateAwaitingReviewReports(organisationId, workspaceId, isConsolidation);
          trackEvent(isConsolidation ? TRACKING_EVENTS.CREATE_CONSOLIDATION_SCHEDULE : TRACKING_EVENTS.CREATE_SCHEDULE);
        }
        invalidateLiveReports(organisationId, workspaceId, !!isConsolidation);
        revalidateData?.();
        handleClose();
        toastSuccess(
          <FormattedMessage id="reporting.schedule_has_been_saved" defaultMessage="Schedule has been saved" />
        );
      } catch (e) {
        Logger.error(e as string);
        toastError();
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleOpenPreviewEmail = () => {
    setIsOpenPreviewEmail(true);
  };

  const handleClosePreviewEmail = () => {
    setIsOpenPreviewEmail(false);
  };

  const handleReportNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReportName(event.target.value);
  };

  const handleRecurrenceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRecurrence = event.target.value as Recurrence;
    setRecurrence(newRecurrence);
    if (newRecurrence === Recurrence.QUARTERLY) {
      setRecurrenceRepeatValue(String(REPEATING_FREQUENCY_QUARTERLY));
    }
    if (newRecurrence === Recurrence.ANNUALLY) {
      setRecurrenceRepeatValue(String(REPEATING_FREQUENCY_ANNUALLY));
    }
  };

  const handleEmailSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailSubject(event.target.value);
  };

  const handleEmailBodyChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEmailBody(event.target.value);
  };

  const handleRecurrenceStartDateChange = (date: Date) => {
    setRecurrenceStartDate(date);
    // If we have start date error, we need to choose start date in future to dismiss this error,
    // only future dates will be allowed for user to choose in this case
    if (recurrenceStartDateError) {
      setRecurrenceStartDateError(false);
    }
  };

  const handleRecurrenceValueChange = (value: string) => setRecurrenceRepeatValue(value);

  const handleRecurrenceEndTypeChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setRecurrenceEndType(event.target.value as RecurrenceEndType);

  const handleOccurancesValueChange = (value: number | string) => setOccurancesValue(`${value}`);

  const handleRecurrenceEndDateChange = (date: Date) => setRecurrenceEndDate(date);

  const handleRecurrenceWeekDayChange = (weekDay: RecurrenceWeekDay) => setRecurrenceWeekDay(weekDay);

  const handleRecurrenceMonthDay = (event: React.ChangeEvent<HTMLInputElement>) =>
    setRecurrenceMonthDay(event.target.value);

  const handleReviewStepChange = (event: React.ChangeEvent<HTMLInputElement>) => setReviewStep(event.target.checked);

  if (isLoading || isInitialLoading || isMembersLoading) {
    return (
      <ReportingSidebar
        aria-label={intl.formatMessage({
          id: 'reporting.schedule_or_send_PDF_report',
          defaultMessage: 'Schedule or send PDF report'
        })}
        title={intl.formatMessage({
          id: 'reporting.schedule_or_send_PDF_report',
          defaultMessage: 'Schedule or send PDF report'
        })}
        open={isOpen}
        onCancel={handleClose}
        sidebarProps={{
          size: 'large'
        }}
      >
        <UILoader />
      </ReportingSidebar>
    );
  }

  const recurrenceEndDateIsBeforeStartDate = isBefore(startOfDay(recurrenceEndDate), startOfDay(recurrenceStartDate));

  const isSubmitDisabled =
    recurrenceStartDateError ||
    !reportName ||
    !selectedUsers.length ||
    !emailSubject ||
    !emailBody ||
    (recurrenceStartDate &&
      recurrenceEndDate &&
      recurrenceEndType === RecurrenceEndType.ON_THIS_DAY &&
      recurrenceEndDateIsBeforeStartDate) ||
    selectedUsers.length > MAX_NUMBER_OF_USERS;

  const recipientsHelpText = intl.formatMessage({
    id: 'predict.only_users_with_access_can_receive_scheduled_reports',
    defaultMessage: 'Only users with access to this organisation can be selected to receive scheduled reports'
  });

  return (
    <>
      <ReportingSidebar
        aria-label={intl.formatMessage({
          id: 'reporting.schedule_or_send_PDF_report',
          defaultMessage: 'Schedule or send PDF report'
        })}
        title={intl.formatMessage({
          id: 'reporting.schedule_or_send_PDF_report',
          defaultMessage: 'Schedule or send PDF report'
        })}
        onCancel={handleClose}
        open={isOpen}
        sidebarProps={{
          size: 'large'
        }}
      >
        <Form
          stickyFooter
          leftSideButtons={
            <Button disabled={isSubmitDisabled} onClick={handleOpenPreviewEmail} buttonType="secondary">
              <FormattedMessage id="reporting.preview_email" defaultMessage="Preview email" />
            </Button>
          }
          saveButton={
            <Button
              buttonType="primary"
              size="small"
              onClick={handleSchedulePdfReport}
              disabled={isSubmitDisabled}
              destructive
            >
              {<FormattedMessage id="reporting.schedule_PDF_report" defaultMessage="Schedule PDF report" />}
            </Button>
          }
          onSubmit={handleSchedulePdfReport}
        >
          <div className={styles.dialogContent}>
            <div className={styles.inlineFieldsCarbon}>
              <div className={styles.reportFieldCarbon}>
                <Textbox
                  label={intl.formatMessage({ id: 'reporting.report', defaultMessage: 'Report' })}
                  placeholder={intl.formatMessage({
                    id: 'reporting.report_name',
                    defaultMessage: 'Report name'
                  })}
                  value={reportName}
                  onChange={handleReportNameChange}
                  required
                />
              </div>
              <div className={styles.recurrenceFieldCarbon}>
                <Select
                  name="recurrence"
                  id="recurrence"
                  label={intl.formatMessage({ id: 'reporting.recurrence', defaultMessage: 'Recurrence' })}
                  size="medium"
                  value={recurrence}
                  onChange={handleRecurrenceChange}
                >
                  <Option
                    text={intl.formatMessage({
                      id: 'reporting.recurrence_does_not_repeat',
                      defaultMessage: 'Does not repeat'
                    })}
                    value={Recurrence.DOES_NOT_REPEAT}
                  />
                  <Option
                    text={intl.formatMessage({ id: 'common.recurrence_daily', defaultMessage: 'Daily' })}
                    value={Recurrence.DAILY}
                  />
                  <Option
                    text={intl.formatMessage({ id: 'common.recurrence_weekly', defaultMessage: 'Weekly' })}
                    value={Recurrence.WEEKLY}
                  />
                  <Option
                    text={intl.formatMessage({ id: 'common.recurrence_monthly', defaultMessage: 'Monthly' })}
                    value={Recurrence.MONTHLY}
                  />
                  <Option
                    text={intl.formatMessage({ id: 'common.recurrence_quarterly', defaultMessage: 'Quarterly' })}
                    value={Recurrence.QUARTERLY}
                  />
                  <Option
                    text={intl.formatMessage({ id: 'common.recurrence_annually', defaultMessage: 'Annually' })}
                    value={Recurrence.ANNUALLY}
                  />
                </Select>
              </div>
            </div>
            {recurrence !== Recurrence.DOES_NOT_REPEAT && (
              <div>
                <div className={styles.labelFont} style={{ marginBottom: 10 }}>
                  <FormattedMessage id="reporting.recurrence" defaultMessage="Recurrence" />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
                  <div style={{ marginRight: 40, fontWeight: 400 }} className={styles.labelFont}>
                    <FormattedMessage id="common.start" defaultMessage="Start" />
                  </div>
                  <DateInput
                    value={intl.formatDate(new Date(recurrenceStartDate))}
                    onChange={e => handleRecurrenceStartDateChange(new Date(e.target.value.rawValue || ''))}
                    size="medium"
                    error={
                      recurrenceStartDateError &&
                      intl.formatMessage({
                        id: 'reporting.reset_start_date_to_today',
                        defaultMessage: 'Reset the start date to today or a future date'
                      })
                    }
                    mb={0}
                    aria-label={intl.formatMessage({
                      id: 'reporting.select_scheduling_start_date.aria_label',
                      defaultMessage: 'Select scheduling start date'
                    })}
                    minDate={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
                  <div style={{ marginRight: 40, fontWeight: 400 }} className={styles.labelFont}>
                    <FormattedMessage id="common.every" defaultMessage="Every" />
                  </div>
                  <UICarbonNumberField
                    value={recurrenceRepeatValue}
                    onChange={handleRecurrenceValueChange}
                    clampMin={1}
                    disabled={recurrence === Recurrence.QUARTERLY || recurrence === Recurrence.ANNUALLY}
                    className={styles.recurrenceValueWidth}
                    mb={0}
                    style={{ marginRight: 8 }}
                  />
                  {recurrence === Recurrence.DAILY ? (
                    <div style={{ fontWeight: 400 }} className={styles.labelFont}>
                      <FormattedMessage id="common.day_s" defaultMessage="day (s)" />
                    </div>
                  ) : recurrence === Recurrence.WEEKLY ? (
                    <div style={{ fontWeight: 400 }} className={styles.labelFont}>
                      <FormattedMessage id="common.week_s" defaultMessage="week (s)" />
                    </div>
                  ) : recurrence === Recurrence.MONTHLY ? (
                    <div style={{ fontWeight: 400 }} className={styles.labelFont}>
                      <FormattedMessage id="common.month_s" defaultMessage="month (s)" />
                    </div>
                  ) : (
                    <div style={{ fontWeight: 400 }} className={styles.labelFont}>
                      <FormattedMessage id="common.months" defaultMessage="months" />
                    </div>
                  )}
                </div>
                {recurrence === Recurrence.WEEKLY && (
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
                    <div style={{ marginRight: 58, fontWeight: 400 }} className={styles.labelFont}>
                      <FormattedMessage id="common.on_day_name" defaultMessage="On" />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div
                        className={classNames(styles.weekDay, {
                          [styles.activeWeekDay]: recurrenceWeekDay === RecurrenceWeekDay.MONDAY
                        })}
                        onClick={() => handleRecurrenceWeekDayChange(RecurrenceWeekDay.MONDAY)}
                      >
                        {intl.formatMessage({ id: 'common.monday_first_letter', defaultMessage: 'M' })}
                      </div>
                      <div
                        className={classNames(styles.weekDay, {
                          [styles.activeWeekDay]: recurrenceWeekDay === RecurrenceWeekDay.TUESDAY
                        })}
                        onClick={() => handleRecurrenceWeekDayChange(RecurrenceWeekDay.TUESDAY)}
                      >
                        {intl.formatMessage({ id: 'common.tuesday_first_letter', defaultMessage: 'T' })}
                      </div>
                      <div
                        className={classNames(styles.weekDay, {
                          [styles.activeWeekDay]: recurrenceWeekDay === RecurrenceWeekDay.WEDNESDAY
                        })}
                        onClick={() => handleRecurrenceWeekDayChange(RecurrenceWeekDay.WEDNESDAY)}
                      >
                        {intl.formatMessage({ id: 'common.wednesday_first_letter', defaultMessage: 'W' })}
                      </div>
                      <div
                        className={classNames(styles.weekDay, {
                          [styles.activeWeekDay]: recurrenceWeekDay === RecurrenceWeekDay.THURSDAY
                        })}
                        onClick={() => handleRecurrenceWeekDayChange(RecurrenceWeekDay.THURSDAY)}
                      >
                        {intl.formatMessage({ id: 'common.thursday_first_letter', defaultMessage: 'T' })}
                      </div>
                      <div
                        className={classNames(styles.weekDay, {
                          [styles.activeWeekDay]: recurrenceWeekDay === RecurrenceWeekDay.FRIDAY
                        })}
                        onClick={() => handleRecurrenceWeekDayChange(RecurrenceWeekDay.FRIDAY)}
                      >
                        {intl.formatMessage({ id: 'common.friday_first_letter', defaultMessage: 'F' })}
                      </div>
                      <div
                        className={classNames(styles.weekDay, {
                          [styles.activeWeekDay]: recurrenceWeekDay === RecurrenceWeekDay.SATURDAY
                        })}
                        onClick={() => handleRecurrenceWeekDayChange(RecurrenceWeekDay.SATURDAY)}
                      >
                        {intl.formatMessage({ id: 'common.saturday_first_letter', defaultMessage: 'S' })}
                      </div>
                      <div
                        className={classNames(styles.weekDay, {
                          [styles.activeWeekDay]: recurrenceWeekDay === RecurrenceWeekDay.SUNDAY
                        })}
                        onClick={() => handleRecurrenceWeekDayChange(RecurrenceWeekDay.SUNDAY)}
                      >
                        {intl.formatMessage({ id: 'common.sunday_first_letter', defaultMessage: 'S' })}
                      </div>
                    </div>
                  </div>
                )}
                {(recurrence === Recurrence.MONTHLY ||
                  recurrence === Recurrence.QUARTERLY ||
                  recurrence === Recurrence.ANNUALLY) && (
                  <div
                    style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}
                    className={styles.recurrenceSelectWidth}
                  >
                    <div style={{ marginRight: 58, fontWeight: 400 }} className={styles.labelFont}>
                      <FormattedMessage id="common.on_nth_day_of_month" defaultMessage="On" />
                    </div>
                    <Select value={recurrenceMonthDay} onChange={handleRecurrenceMonthDay} mb={0}>
                      <Option
                        text={intl.formatMessage({ id: 'common.the_last', defaultMessage: 'the last' })}
                        value={THE_LAST}
                      />
                      {[...Array(32).keys()].slice(1).map(i => (
                        <Option key={`option_${i}`} text={getOrdinalSuffix(i, intl.locale)} value={String(i)} />
                      ))}
                    </Select>
                    <span style={{ marginLeft: 8 }}>
                      <FormattedMessage id="reporting.day_of_month" defaultMessage="day of the month" />
                    </span>
                  </div>
                )}
                <div
                  className={classNames(styles.recurrenceEndRow, styles.recurrenceSelectWidth, {
                    [styles.recurrenceEndRowWithDate]: recurrenceEndType === RecurrenceEndType.ON_THIS_DAY
                  })}
                >
                  <div className={classNames(styles.labelFont, styles.recurrenceEndLabel)}>
                    <FormattedMessage id="common.end" defaultMessage="End" />
                  </div>
                  <Select value={recurrenceEndType} onChange={handleRecurrenceEndTypeChange} mb={0}>
                    <Option
                      text={intl.formatMessage({
                        id: 'reporting.on_this_day',
                        defaultMessage: 'on this day'
                      })}
                      value={RecurrenceEndType.ON_THIS_DAY}
                    />
                    <Option
                      text={intl.formatMessage({ id: 'common.after_n_occurrences', defaultMessage: 'after' })}
                      value={RecurrenceEndType.AFTER_N_OCCURANCES}
                    />
                    <Option
                      text={intl.formatMessage({ id: 'reporting.no_end_date', defaultMessage: 'no end date' })}
                      value={RecurrenceEndType.NO_END_DATE}
                    />
                  </Select>
                  {recurrenceEndType === RecurrenceEndType.AFTER_N_OCCURANCES && (
                    <>
                      <UICarbonNumberField
                        aria-label={intl.formatMessage({
                          id: 'reporting.enter_occurrences_number.aria_label',
                          defaultMessage: 'Enter occurrences number'
                        })}
                        value={occurancesValue}
                        onChange={handleOccurancesValueChange}
                        className={styles.recurrenceValueWidth}
                        mb={0}
                        style={{ marginRight: 8, marginLeft: 8 }}
                      />
                      <FormattedMessage
                        id="common.value_recurrences"
                        defaultMessage="{count, plural, one {# occurrence} other {# occurrences}}"
                        values={{ count: occurancesValue }}
                      />
                    </>
                  )}
                  {recurrenceEndType === RecurrenceEndType.ON_THIS_DAY && (
                    <DateInput
                      value={intl.formatDate(new Date(recurrenceEndDate))}
                      onChange={e => handleRecurrenceEndDateChange(new Date(e.target.value.rawValue || ''))}
                      size="medium"
                      error={
                        recurrenceEndDateIsBeforeStartDate &&
                        intl.formatMessage({
                          id: 'reporting.reset_end_date_future_date',
                          defaultMessage: 'Reset the end date to a future date'
                        })
                      }
                      mb={0}
                      aria-label={intl.formatMessage({
                        id: 'reporting.select_scheduling_end_date.aria_label',
                        defaultMessage: 'Select scheduling end date'
                      })}
                      minDate={new Date().toISOString().split('T')[0]}
                      ml="16px"
                      validationMessagePositionTop
                    />
                  )}
                </div>
                <div className={styles.labelFont} style={{ marginBottom: 12 }}>
                  <FormattedMessage id="reporting.review_step" defaultMessage="Review step" />
                </div>
                <div
                  className={styles.labelFont}
                  style={{ marginBottom: 30, fontWeight: 400, display: 'flex', alignItems: 'center' }}
                >
                  <Checkbox
                    aria-label={intl.formatMessage({
                      id: 'reporting.review_report_before_sending',
                      defaultMessage: 'Review report before sending (an email will be sent to you)'
                    })}
                    checked={reviewStep}
                    onChange={handleReviewStepChange}
                    mb={0}
                    mr={1}
                    size="large"
                  />
                  <FormattedMessage
                    id="reporting.review_report_before_sending"
                    defaultMessage="Review report before sending (an email will be sent to you)"
                  />
                </div>
              </div>
            )}
            <div>
              <div className={styles.recipientsLabel}>
                <label id="account-label" htmlFor="account">
                  {intl.formatMessage({ id: 'common.to_users', defaultMessage: 'To' })}
                  <span className={styles.requiredMarker} aria-hidden="true">
                    {'*'}
                  </span>
                </label>
                <Tooltip message={recipientsHelpText} position="top">
                  <button type="button" className={styles.recipientsHelp} aria-label={recipientsHelpText}>
                    <Icon type="question" fontSize="small" aria-hidden />
                  </button>
                </Tooltip>
              </div>
              <MultiSelect
                name="account"
                id="account"
                aria-labelledby="account-label"
                placeholder={
                  selectedUsers.length
                    ? ''
                    : intl.formatMessage({
                        id: 'predict.start_typing_find_users',
                        defaultMessage: 'Start typing to find users'
                      })
                }
                value={selectedUsers}
                onChange={e => {
                  if (typeof e.target.value === 'object') {
                    setSelectedUsers(e.target.value);
                  }
                }}
                required
              >
                {memberOptions.map(m => (
                  <Option key={m.id} text={m.text} value={m} />
                ))}
              </MultiSelect>
            </div>
            <Textbox
              label={intl.formatMessage({ id: 'common.subject', defaultMessage: 'Subject' })}
              value={emailSubject}
              onChange={handleEmailSubjectChange}
              aria-label={intl.formatMessage({ id: 'common.subject', defaultMessage: 'Subject' })}
              required
            />
            <Textarea
              label={intl.formatMessage({ id: 'common.body', defaultMessage: 'Body' })}
              expandable
              value={emailBody}
              onChange={handleEmailBodyChange}
              aria-label="Email body"
              rows={8}
              required
            />
          </div>
        </Form>
      </ReportingSidebar>
      {isOpenPreviewEmail && (
        <EmailPreview
          isOpen={isOpenPreviewEmail}
          handleClose={handleClosePreviewEmail}
          emailSubject={emailSubject}
          emailBody={emailBody}
        />
      )}
    </>
  );
};
